import React, { useState } from "react";
import { X } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useContactForm from "../../services/LandingPage/useContactForm";

const FreeTrial = ({ show, handleClose, pageKey }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    website: "",
    contact: "",
  });

  const mobileRegex = /^[6-9]\d{9}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const { submitForm, loading } = useContactForm();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Required fields
    if (!formData.fullName || !formData.email || !formData.contact) {
      toast.warning("Please fill all required fields");
      return;
    }

    // Email validation
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Mobile validation
    if (!mobileRegex.test(formData.contact)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    // API call
    const result = await submitForm({
      fullName: formData.fullName,
      workEmail: formData.email,
      website: formData.website,
      phoneNumber: formData.contact,
    });

    if (result.success) {
      toast.success(result.message);

      if (pageKey) {
        sessionStorage.setItem(`freeTrialClosed_${pageKey}`, "true");
      }

      setFormData({ fullName: "", email: "", website: "", contact: "" });

      setTimeout(() => {
        handleClose();
      }, 1500);
    } else {
      toast.error(result.message);
    }
  };

  const handleModalClose = () => {
    if (pageKey) {
      sessionStorage.setItem(`freeTrialClosed_${pageKey}`, "true");
    }
    handleClose();
  };

  if (!show) return null;

  return (
    <>
<ToastContainer/>
      <div style={overlayStyle}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <style>{`
            @keyframes modalFadeIn {
              from {
                opacity: 0;
                transform: translateY(-20px) scale(0.98);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
          `}</style>

          <div style={{ padding: "1.25rem", position: "relative" }}>
            <button onClick={handleModalClose} style={closeButtonStyle}>
              <X size={16} />
            </button>

            <h1
              style={{
                fontWeight: "700",
                marginBottom: "0.5rem",
                textAlign: "center",
                color: "#01C37D",
                fontSize: "1.8rem",
              }}
            >
              Start Your Free Trial
            </h1>

            <p
              style={{
                color: "#6c757d",
                textAlign: "center",
                marginBottom: "1rem",
                fontSize: "14px",
              }}
            >
              Please fill out the form below and we will ensure a personalised
              demo catering to your requirements.
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "0.75rem" }}>
                <label style={labelStyle}>
                  Full Name <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (/^[a-zA-Z\s]*$/.test(value)) {
                      setFormData((prev) => ({
                        ...prev,
                        fullName: value,
                      }));
                    }
                  }}
                  placeholder="Enter Full Name"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: "0.75rem" }}>
                <label style={labelStyle}>
                  Work Email <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({ ...prev, email: value }));

                    // optional live validation
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                    if (value && !emailRegex.test(value)) {
                      toast.warning("Invalid email format");
                    }
                  }}
                  placeholder="Enter Work Email"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: "0.75rem" }}>
                <label style={labelStyle}>Website (Optional)</label>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="www.website.com"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label style={labelStyle}>
                  Contact Number <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, ""); // only digits

                    if (value.length > 10) return;

                    setFormData((prev) => ({ ...prev, contact: value }));
                  }}
                  maxLength={10}
                  placeholder="Contact Number"
                  style={inputStyle}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={submitButtonStyle}
                >
                  {loading ? "Submitting..." : "Start Free Trial"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.55)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1rem",
};

const modalStyle = {
  maxWidth: "500px",
  width: "100%",
  background: "#fff",
  borderRadius: "18px",
  position: "relative",
  animation: "modalFadeIn 0.3s ease-out",
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
};

const closeButtonStyle = {
  position: "absolute",
  top: "12px",
  right: "12px",
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  border: "none",
  background: "#f5f5f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const labelStyle = {
  color: "#02744a",
  fontWeight: 500,
  marginBottom: "6px",
  display: "inline-block",
};

const inputStyle = {
  width: "100%",
  padding: "0.75rem",
  border: "1px solid #ced4da",
  borderRadius: "10px",
  fontSize: "1rem",
  boxSizing: "border-box",
  outline: "none",
};

const submitButtonStyle = {
  width: "100%",
  padding: "0.85rem",
  backgroundColor: "#03a167",
  border: "none",
  borderRadius: "999px",
  color: "white",
  fontWeight: "600",
  cursor: "pointer",
};

export default FreeTrial;
